import React, { useState, useRef, useEffect } from 'react';
import { Layers, BookOpen, Trophy, MoreHorizontal, ArrowLeft, BarChart2, Wand2, Settings as SettingsIcon, ClipboardCheck, Globe, Users } from 'lucide-react';
import ChatInterface from './ChatInterface';
import Lessons from './Lessons';
import CategorySelection from './CategorySelection';
import BattlePass from './BattlePass';
import StatusDashboard from './StatusDashboard';
import Settings from './Settings';
import AvatarGenerator from './AvatarGenerator';
import Assessment from './Assessment';
import ImpactDashboard from './ImpactDashboard';
import TeamDashboard from './TeamDashboard';
import { UserStats, Message, Lesson, BattlePassReward, Category, ChatStyle, Language } from '../types';

interface ExpandableWindowProps {
  isExpanded: boolean;
  messages: Message[];
  selectedCategory: Category | null;
  categories: Category[];
  rewards: BattlePassReward[];
  userStats: UserStats;
  onSendMessage: (message: string, language: Language) => void;
  onSelectLesson: (lesson: Lesson, earnedXp: number) => void;
  onSelectCategory: (category: Category) => void;
  onBackToCategories: () => void;
  isTyping: boolean;
  onClaimReward: (rewardId: string) => void;
  onUpdateAvatar: (index: number) => void;
  chatStyle: ChatStyle;
  onChangeChatStyle: (style: ChatStyle) => void;
}

type TabType = 'chat' | 'assessment' | 'lessons' | 'growthpass' | 'status' | 'settings' | 'avatar-generator' | 'impact' | 'team';

const ExpandableWindow: React.FC<ExpandableWindowProps> = ({
  isExpanded,
  messages,
  selectedCategory,
  categories,
  rewards,
  userStats,
  onSendMessage,
  onSelectLesson,
  onSelectCategory,
  onBackToCategories,
  isTyping,
  onClaimReward,
  onUpdateAvatar,
  chatStyle,
  onChangeChatStyle
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('chat');
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: window.innerWidth - 480, y: 42 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [showOverflow, setShowOverflow] = useState(false);
  const windowRef = useRef<HTMLDivElement>(null);
  const overflowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      const maxX = window.innerWidth - (windowRef.current?.offsetWidth || 440);
      const maxY = window.innerHeight - (windowRef.current?.offsetHeight || 448);
      
      setPosition(prev => ({
        x: Math.min(Math.max(0, prev.x), maxX),
        y: Math.min(Math.max(0, prev.y), maxY)
      }));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (overflowRef.current && !overflowRef.current.contains(event.target as Node)) {
        setShowOverflow(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    const rect = windowRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();

    const maxX = window.innerWidth - (windowRef.current?.offsetWidth || 440);
    const maxY = window.innerHeight - (windowRef.current?.offsetHeight || 448);
    
    const newX = Math.min(Math.max(0, e.clientX - dragOffset.x), maxX);
    const newY = Math.min(Math.max(0, e.clientY - dragOffset.y), maxY);
    
    setPosition({ x: newX, y: newY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    const rect = windowRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    e.preventDefault();

    const maxX = window.innerWidth - (windowRef.current?.offsetWidth || 440);
    const maxY = window.innerHeight - (windowRef.current?.offsetHeight || 448);
    
    const newX = Math.min(Math.max(0, e.touches[0].clientX - dragOffset.x), maxX);
    const newY = Math.min(Math.max(0, e.touches[0].clientY - dragOffset.y), maxY);
    
    setPosition({ x: newX, y: newY });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  if (!isExpanded) return null;

  const mainTabs = [
    { id: 'chat', label: 'Chat', icon: <Layers className="w-5 h-5" />, tooltip: 'Chat' },
    { id: 'assessment', label: 'Assessment', icon: <ClipboardCheck className="w-5 h-5" />, tooltip: 'Assessment' },
    { id: 'lessons', label: 'Lessons', icon: <BookOpen className="w-5 h-5" />, tooltip: 'Lessons' },
    { id: 'growthpass', label: 'Growth Pass', icon: <Trophy className="w-5 h-5" />, tooltip: 'Growth Pass' },
    { id: 'impact', label: 'Impact', icon: <Globe className="w-5 h-5" />, tooltip: 'Impact' },
    { id: 'team', label: 'Team', icon: <Users className="w-5 h-5" />, tooltip: 'Team Dashboard' },
  ];

  const overflowTabs = [
    { id: 'status', label: 'Status', icon: <BarChart2 className="w-5 h-5" />, tooltip: 'Status' },
    { id: 'avatar-generator', label: 'Avatar Generator', icon: <Wand2 className="w-5 h-5" />, tooltip: 'Avatar Generator' },
    { id: 'settings', label: 'Settings', icon: <SettingsIcon className="w-5 h-5" />, tooltip: 'Settings' },
  ];

  const hasNotifications = rewards.some(reward => !reward.claimed && userStats.level >= reward.level);

  return (
    <div 
      ref={windowRef}
      className="fixed z-50 touch-none"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: '100%',
        maxWidth: '440px',
        transform: isDragging ? 'scale(1.02)' : 'scale(1)',
        transition: isDragging ? 'none' : 'transform 0.2s ease-out',
      }}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div 
        className={`
          w-full h-[448px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden
          transition-shadow duration-150 ease-out animate-slide-up
          ${isDragging ? 'shadow-[0_0_0_4px_rgba(59,130,246,0.5)]' : 'hover:shadow-[0_0_0_2px_rgba(59,130,246,0.3)]'}
        `}
      >
        <div 
          className="flex items-center bg-gray-50 px-2 py-1.5 cursor-move select-none"
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <div className="flex space-x-1 flex-1">
            {mainTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`
                  group relative flex items-center justify-center w-10 h-10 rounded-lg transition-colors
                  ${activeTab === tab.id 
                    ? 'bg-blue-500 text-white' 
                    : 'text-gray-600 hover:bg-gray-200'
                  }
                `}
                title={tab.tooltip}
              >
                {tab.icon}
                {tab.id === 'growthpass' && hasNotifications && (
                  <div className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full" />
                )}
              </button>
            ))}
          </div>

          <div className="relative" ref={overflowRef}>
            <button
              onClick={() => setShowOverflow(!showOverflow)}
              className={`
                group relative flex items-center justify-center w-10 h-10 rounded-lg transition-colors
                ${showOverflow ? 'bg-gray-200' : 'hover:bg-gray-200'}
              `}
              title="More options"
            >
              <MoreHorizontal className="w-5 h-5 text-gray-600" />
            </button>

            {showOverflow && (
              <div 
                className="absolute right-0 top-12 w-48 bg-white rounded-lg shadow-lg py-1 border border-gray-200 z-[60]"
              >
                {overflowTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id as TabType);
                      setShowOverflow(false);
                    }}
                    className={`
                      w-full flex items-center px-4 py-2 text-sm transition-colors
                      ${activeTab === tab.id 
                        ? 'bg-blue-50 text-blue-600' 
                        : 'text-gray-700 hover:bg-gray-50'
                      }
                    `}
                  >
                    {tab.icon}
                    <span className="ml-3">{tab.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          {activeTab === 'chat' && (
            <ChatInterface 
              messages={messages} 
              onSendMessage={onSendMessage} 
              isTyping={isTyping}
              chatStyle={chatStyle}
              onChangeChatStyle={onChangeChatStyle}
            />
          )}
          
          {activeTab === 'assessment' && (
            <Assessment />
          )}

          {activeTab === 'lessons' && (
            <div className="h-full">
              {selectedCategory ? (
                <div className="h-full">
                  <button
                    onClick={onBackToCategories}
                    className="mb-4 flex items-center text-gray-600 hover:text-gray-800"
                  >
                    <ArrowLeft className="w-5 h-5 mr-2" />
                    Back to Categories
                  </button>
                  <Lessons 
                    lessons={selectedCategory.lessons}
                    onSelectLesson={onSelectLesson}
                  />
                </div>
              ) : (
                <CategorySelection
                  categories={categories}
                  onSelectCategory={onSelectCategory}
                />
              )}
            </div>
          )}
          
          {activeTab === 'growthpass' && (
            <BattlePass 
              rewards={rewards} 
              userStats={userStats}
              onClaimReward={onClaimReward}
            />
          )}

          {activeTab === 'impact' && (
            <ImpactDashboard userStats={userStats} />
          )}

          {activeTab === 'team' && (
            <TeamDashboard />
          )}
          
          {activeTab === 'status' && (
            <StatusDashboard 
              userStats={userStats}
            />
          )}

          {activeTab === 'avatar-generator' && (
            <div className="h-full overflow-y-auto">
              <AvatarGenerator onComplete={onUpdateAvatar} />
            </div>
          )}

          {activeTab === 'settings' && (
            <Settings
              userStats={userStats}
              onUpdateAvatar={onUpdateAvatar}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpandableWindow;